import {GridRowId} from '@mui/x-data-grid-pro';
import moment from 'moment';
import {FormikErrors} from 'formik';
import {
  ActionEnum,
  BulkAdminSortableFieldsEnum,
  DateRangeInput,
  SelectPropositionTypeEnum,
  SortingDirectionEnum,
} from '../../schema-types/globalTypes';
import {initialBulkAdminSortable, initialSortingDirection} from './useBulkAdmin';
import {FieldValue, Option} from '../../components/AutoForm/AutoFieldNew';
import {UPDATES} from '../../shared/UPDATES';
import {SetFieldValueWithSourceKey} from '../../components/AutoForm/useAutoForm';
import {GetBulkAccountsVariables} from '../../schema-types/GetBulkAccounts';
import {useRTQ} from '../../hooks/useRTQ';

export const useBulkFiltersList = (): Option<FieldValue>[] => {
  const {atrName} = useRTQ();
  return [
    {value: 'adviserEmails', label: 'Adviser'},
    {value: 'ownerEmails', label: 'Owner'},
    {value: 'propositionTypes', label: 'Proposition type'},
    {value: 'nextReviewDateRange', label: 'Next annual review date'},
    {value: 'lastAtrDateRange', label: `${atrName} date`},
    {value: 'cqUpdatedDateRange', label: 'CQ updated'},
  ];
};

export const propositionTypeOptions: Option<FieldValue>[] = [
  {value: SelectPropositionTypeEnum.SELECT_CONVERSIONS, label: 'Select Conversions'},
  {value: SelectPropositionTypeEnum.SELECT_SAM_ONLY, label: 'Select SAM Only'},
  {value: SelectPropositionTypeEnum.SELECT_TRANSIT, label: 'Select Transit'},
  {value: SelectPropositionTypeEnum.SELECT_WITH_FP, label: 'Select with FP'},
];

export interface BulkAdminValues {
  search?: string;
  isBulkActionsModalOpen: boolean;
  activeFilter: Option<FieldValue> | undefined;
  selectedIDs: GridRowId[];
  orderBy: BulkAdminSortableFieldsEnum;
  cursors: string[];
  orderDirection: SortingDirectionEnum;
  selectedActions: (ActionEnum | null)[];
  propositionTypes?: SelectPropositionTypeEnum[];
  adviserEmails?: string[];
  ownerEmails?: string[];
  nextReviewDateFrom?: string;
  lastAtrDateFrom?: string;
  cqUpdatedDateFrom?: string;
  nextReviewDateTo?: string;
  lastAtrDateTo?: string;
  cqUpdatedDateTo?: string;
}

export const initialBulkAdminValues: BulkAdminValues = {
  isBulkActionsModalOpen: false,
  search: undefined,
  activeFilter: undefined,
  selectedIDs: [],
  cursors: [],
  selectedActions: [],
  orderBy: initialBulkAdminSortable,
  orderDirection: initialSortingDirection,
  adviserEmails: undefined,
  ownerEmails: undefined,
  propositionTypes: undefined,
  nextReviewDateFrom: undefined,
  lastAtrDateFrom: undefined,
  cqUpdatedDateFrom: undefined,
  nextReviewDateTo: undefined,
  lastAtrDateTo: undefined,
  cqUpdatedDateTo: undefined,
};

export const getDateRangeInputFromTwofields = ({
  to,
  from,
}: {
  to: string;
  from: string;
}): DateRangeInput => {
  return {
    dateFrom: from,
    dateTo: to,
  };
};

/**
 * DateRangeInput accepts non-empty strings only,
 * hence why we only populate the property
 * if both to and from values are non-falsy
 * @param values
 * @returns
 */
export const getBulkAdminDateRanges = (values: BulkAdminValues) => {
  const payload: Partial<GetBulkAccountsVariables> = {};
  if (!!values.nextReviewDateFrom && !!values.nextReviewDateTo) {
    payload.nextReviewDateRange = getDateRangeInputFromTwofields({
      from: moment.utc(values.nextReviewDateFrom).format('YYYY-MM-DD'),
      to: moment.utc(values.nextReviewDateTo).format('YYYY-MM-DD'),
    });
  }
  if (!!values.lastAtrDateFrom && !!values.lastAtrDateTo) {
    payload.lastAtrDateRange = getDateRangeInputFromTwofields({
      from: moment.utc(values.lastAtrDateFrom).format('YYYY-MM-DD'),
      to: moment.utc(values.lastAtrDateTo).format('YYYY-MM-DD'),
    });
  }
  if (!!values.cqUpdatedDateFrom && !!values.cqUpdatedDateTo) {
    payload.cqUpdatedDateRange = getDateRangeInputFromTwofields({
      from: moment.utc(values.cqUpdatedDateFrom).format('YYYY-MM-DD'),
      to: moment.utc(values.cqUpdatedDateTo).format('YYYY-MM-DD'),
    });
  }
  return payload;
};

export const NO_ADVISER_EMAIL = 'noAdviser';

export const getBulkAccountsVariablesFromValues = (
  values: BulkAdminValues
): GetBulkAccountsVariables => {
  const {
    orderBy,
    orderDirection,
    search: searchTerm,
    adviserEmails,
    ownerEmails,
    propositionTypes,
  } = values;

  const noAdviser = adviserEmails?.includes(NO_ADVISER_EMAIL);

  return {
    searchTerm,
    orderBy,
    orderDirection,
    propositionTypes,
    adviserEmails,
    ownerEmails,
    noAdviser,
    ...getBulkAdminDateRanges(values),
  };
};

export const clearFieldValue = ({
  setFieldValue,
  filter,
}: {
  setFieldValue: SetFieldValueWithSourceKey<BulkAdminValues>;
  filter?: Option<FieldValue>;
}) => {
  if (`${filter?.value}`.toLowerCase().includes('range')) {
    setFieldValue(UPDATES._1200, `${filter?.value}`.replace('Range', 'To'), undefined);
    setFieldValue(UPDATES._1200, `${filter?.value}`.replace('Range', 'From'), undefined);
  } else {
    setFieldValue(UPDATES._1200, `${filter?.value}`, undefined);
  }
};

export const removeDateFilterSelection = (
  filter: string,
  setFieldValue: SetFieldValueWithSourceKey<BulkAdminValues>
) => {
  switch (filter) {
    case 'nextReviewDateRange':
      setFieldValue(UPDATES._1201, 'nextReviewDateFrom', undefined);
      setFieldValue(UPDATES._1201, 'nextReviewDateTo', undefined);
      break;
    case 'lastAtrDateRange':
      setFieldValue(UPDATES._1201, 'lastAtrDateFrom', undefined);
      setFieldValue(UPDATES._1201, 'lastAtrDateTo', undefined);
      break;
    case 'cqUpdatedDateRange':
      setFieldValue(UPDATES._1201, 'cqUpdatedDateFrom', undefined);
      setFieldValue(UPDATES._1201, 'cqUpdatedDateTo', undefined);
      break;
    default:
      break;
  }
};
